<template>
  <RevCard class="h-full p-24">
    <div class="mb-24 flex flex-wrap items-center justify-between gap-24">
      <div class="heading-2 flex items-center gap-8">
        <IconShoppingBag />
        {{ i18n(translations.saleInsights) }}
      </div>

      <div class="flex items-center gap-24">
        <RevInputSelect
          id="sales-metrics-time-period"
          v-model="timePeriodRef"
          :label="i18n(translations.saleInsightsTimePeriod)"
          :options="salesMetricsMetadata?.timePeriods ?? []"
          size="small"
        />
        <RevButton
          size="small"
          :to="{ name: ROUTE_NAMES.INSIGHTS.INDEX }"
          variant="secondary"
        >
          {{ i18n(translations.viewDetails) }}
        </RevButton>
      </div>
    </div>

    <div>
      <RevLoadingScreen
        v-if="isLoading"
        class="mt-32"
        :text="i18n(translations.loading)"
      />

      <div v-else-if="hasNoDataError">
        <div class="body-1">
          {{ i18n(translations.noDataForSelectedPeriod) }}
        </div>
      </div>

      <div v-else class="grid grid-cols-1 gap-24 lg:grid-cols-3">
        <InsightBlock
          v-if="salesMetrics?.receivedOrders"
          :metricData="salesMetrics.receivedOrders"
          :timePeriod="salesMetrics.timePeriods"
        >
          <template #headingSection>
            <IconShoppingBag />
            {{ i18n(translations.salesMetricsOrderBlockTitle) }}
          </template>
        </InsightBlock>

        <InsightBlock
          v-if="salesMetrics?.salesRevenue"
          :metricData="salesMetrics.salesRevenue"
          :timePeriod="salesMetrics.timePeriods"
        >
          <template #headingSection>
            <IconMoney />
            {{ i18n(translations.salesMetricsSalesRevenueBlockTitle) }}
          </template>
        </InsightBlock>

        <InsightBlock
          v-if="salesMetrics?.refundedAmount"
          :metricData="salesMetrics.refundedAmount"
          :timePeriod="salesMetrics.timePeriods"
        >
          <template #headingSection>
            <IconRefund />
            {{ i18n(translations.salesMetricsTotalRefundBlockTitle) }}
          </template>
        </InsightBlock>
      </div>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage, useAsyncData } from '#imports'
import { computed, ref, watch } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { IconMoney } from '@ds/icons/IconMoney'
import { IconRefund } from '@ds/icons/IconRefund'
import { IconShoppingBag } from '@ds/icons/IconShoppingBag'

import type { SalesMetricsTimePeriods } from '~/api/sales-metrics/types'
import { ROUTE_NAMES } from '~/constants/routes'

import translations from '../SaleInsights/SaleInsights.translations'

import InsightBlock from './components/InsightBlock.vue'

const i18n = useI18n()
const { openErrorToast } = useTheToast()

const httpFetch = useHttpFetch()

const timePeriodRef = ref<SalesMetricsTimePeriods>('LAST_7_DAYS')

const { data: salesMetricsMetadata } = useAsyncData(async () => {
  const response = await httpFetch(
    '/api/seller-experience/sales-metrics/metadata',
  )

  return response
})

const {
  data: salesMetrics,
  error,
  pending: isLoading,
} = useAsyncData(
  async () => {
    const response = await httpFetch('/api/seller-experience/sales-metrics', {
      query: {
        timePeriod: timePeriodRef.value,
      },
    })

    return response
  },
  {
    watch: [timePeriodRef],
  },
)

const hasNoDataError = computed(() => {
  return error.value?.statusCode === 404
})

watch(error, (newError) => {
  if (newError && newError.statusCode !== 404) {
    openErrorToast({
      content: getApiErrorMessage(newError as unknown as Error),
    })
  }
})
</script>
